import apiClient from "./api";

export type NotificationProvider = "Smtp" | "Telegram" | "Slack" | "Teams";

export const NOTIFICATION_PROVIDERS: NotificationProvider[] = [
  "Smtp",
  "Telegram",
  "Slack",
  "Teams",
];

/** Who the AI's send_email / send_telegram tools may deliver to on a channel. */
export type NotificationRecipientPolicy = "SignedInUser" | "AllowList" | "Any";

export const NOTIFICATION_RECIPIENT_POLICIES: NotificationRecipientPolicy[] = [
  "SignedInUser",
  "AllowList",
  "Any",
];

export interface SmtpSettings {
  host: string;
  port: number;
  enableSsl: boolean;
  username: string;
  fromEmail: string;
  displayName: string;
}

/** Telegram settings as returned — the bot token never comes back. */
export interface TelegramSettingsSummary {
  botUsername: string;
  hasBotToken: boolean;
}

/** Slack settings as returned — the bot token and signing secret never come back. */
export interface SlackSettingsSummary {
  hasBotToken: boolean;
  /** A signing secret is stored; needed for buttons, replies and commands. */
  hasSigningSecret: boolean;
}

/** Microsoft Teams settings as returned — the client secret never comes back. */
export interface TeamsSettingsSummary {
  /** The Azure Bot's Microsoft App ID. */
  appId: string;
  /** The Microsoft Entra (Directory) tenant ID the bot belongs to. */
  tenantId: string;
  hasClientSecret: boolean;
}

/**
 * Where a channel receives button clicks, replies and commands. Absent when the
 * server has no public URL — the channel then only sends.
 */
export interface NotificationInboundSetup {
  /** Registered with Telegram automatically on save. */
  telegramWebhookUrl?: string | null;
  slackEventsUrl?: string | null;
  slackInteractionsUrl?: string | null;
  slackCommandsUrl?: string | null;
  /** Slack app manifest (JSON) with the URLs, scopes and commands filled in. */
  slackManifest?: string | null;
  /** Paste into the Azure Bot's Configuration → Messaging endpoint. */
  teamsMessagingEndpoint?: string | null;
  /** Opens a 1:1 chat with the bot in Teams once the app is installed. */
  teamsChatLink?: string | null;
}

export interface NotificationChannel {
  id: string;
  name: string;
  providerType: NotificationProvider;
  status: string;
  isActive: boolean;
  smtpSettings?: SmtpSettings | null;
  telegramSettings?: TelegramSettingsSummary | null;
  slackSettings?: SlackSettingsSummary | null;
  teamsSettings?: TeamsSettingsSummary | null;
  /** Set for Telegram, Slack and Teams channels. */
  inboundSetup?: NotificationInboundSetup | null;
  recipientPolicy?: NotificationRecipientPolicy;
  /** Addresses and `@domain` entries, used when the policy is AllowList. */
  allowedRecipients?: string[] | null;
}

export interface SmtpSettingsRequest {
  host: string;
  port: number;
  enableSsl: boolean;
  username?: string | null;
  /** Omit on update to keep the stored password. */
  password?: string | null;
  fromEmail?: string | null;
  displayName?: string | null;
}

export interface TelegramSettingsRequest {
  /** Omit on update to keep the stored token. */
  botToken?: string | null;
  botUsername?: string | null;
}

export interface SlackSettingsRequest {
  /** Omit on update to keep the stored token. */
  botToken?: string | null;
  /** Omit on update to keep the stored signing secret. */
  signingSecret?: string | null;
}

export interface TeamsSettingsRequest {
  appId?: string | null;
  /** Omit on update to keep the stored client secret. */
  clientSecret?: string | null;
  tenantId?: string | null;
}

/** Fill in only the settings block matching providerType. */
export interface CreateChannelRequest {
  name: string;
  providerType: NotificationProvider;
  smtpSettings?: SmtpSettingsRequest;
  telegramSettings?: TelegramSettingsRequest;
  slackSettings?: SlackSettingsRequest;
  teamsSettings?: TeamsSettingsRequest;
  /** Omit for SignedInUser. */
  recipientPolicy?: NotificationRecipientPolicy;
  allowedRecipients?: string[];
}

export interface UpdateChannelRequest {
  name?: string;
  smtpSettings?: SmtpSettingsRequest;
  telegramSettings?: TelegramSettingsRequest;
  slackSettings?: SlackSettingsRequest;
  teamsSettings?: TeamsSettingsRequest;
  /** Omit to keep the stored policy. */
  recipientPolicy?: NotificationRecipientPolicy;
  /** Omit to keep the stored list; an empty array clears it. */
  allowedRecipients?: string[];
}

export interface WidgetNotificationChannelLink {
  notificationChannelId: string;
  isDefault: boolean;
}

export interface WidgetNotificationSettings {
  enabled: boolean;
  channelLinks: WidgetNotificationChannelLink[];
}

export interface WidgetChannelLinkResult {
  notificationChannelId: string;
  channelName: string;
  providerType: string;
  isDefault: boolean;
  isActive: boolean;
}

export interface WidgetNotificationSettingsResult {
  notificationEnabled: boolean;
  channelLinks: WidgetChannelLinkResult[];
}

export interface NotificationLog {
  id: string;
  notificationChannelId: string;
  providerType: string;
  chatSessionId?: string;
  widgetId: string;
  toolCallId?: string;
  recipientAddress: string;
  subject: string;
  status: string;
  errorMessage?: string;
  isWorkflowExecution: boolean;
  workflowId?: string;
  sentAt: string;
}

export interface NotificationLogsPage {
  items: NotificationLog[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Fetches a Teams app package and hands it to the browser as a download, named
 * as the server suggests. A failed request's body arrives as a Blob, so it is
 * read back into `{ error }` for the caller's usual error message.
 */
export const downloadTeamsPackageFrom = async (path: string): Promise<void> => {
  try {
    const response = await apiClient.get(path, { responseType: "blob" });
    const disposition = String(response.headers["content-disposition"] ?? "");
    const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = named ? decodeURIComponent(named[1]) : "teams-app.zip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const body = err.response?.data;
    if (body instanceof Blob) {
      const text = await body.text();
      let parsed: unknown = text;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Plain-text body — used as the message below.
      }
      err.response.data =
        typeof parsed === "string" ? { error: parsed } : parsed;
    }
    throw err;
  }
};

export const notificationService = {
  listChannels: async (): Promise<NotificationChannel[]> => {
    const response = await apiClient.get("/notifications/channels");
    return response.data;
  },

  createChannel: async (
    request: CreateChannelRequest,
  ): Promise<NotificationChannel> => {
    const response = await apiClient.post("/notifications/channels", request);
    return response.data;
  },

  updateChannel: async (
    channelId: string,
    request: UpdateChannelRequest,
  ): Promise<NotificationChannel> => {
    const response = await apiClient.put(
      `/notifications/channels/${channelId}`,
      request,
    );
    return response.data;
  },

  deleteChannel: async (channelId: string): Promise<void> => {
    await apiClient.delete(`/notifications/channels/${channelId}`);
  },

  testConnection: async (
    channelId: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const response = await apiClient.post(
      `/notifications/channels/${channelId}/test`,
    );
    return response.data;
  },

  toggleActive: async (channelId: string): Promise<NotificationChannel> => {
    const response = await apiClient.post(
      `/notifications/channels/${channelId}/toggle-active`,
    );
    return response.data;
  },

  /** Downloads a Teams channel's app package (manifest and icons) for the Teams admin center. */
  downloadTeamsPackage: (channelId: string): Promise<void> =>
    downloadTeamsPackageFrom(
      `/notifications/channels/${channelId}/teams-package`,
    ),

  getWidgetNotificationSettings: async (
    widgetId: string,
  ): Promise<WidgetNotificationSettingsResult> => {
    const response = await apiClient.get(
      `/widget/${widgetId}/notification-settings`,
    );
    return response.data;
  },

  updateWidgetNotificationSettings: async (
    widgetId: string,
    settings: WidgetNotificationSettings,
  ): Promise<void> => {
    await apiClient.put(`/widget/${widgetId}/notification-settings`, settings);
  },

  getLogs: async (params: {
    widgetId?: string;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }): Promise<NotificationLogsPage> => {
    const response = await apiClient.get("/notifications/logs", { params });
    return response.data;
  },
};
